/**
 * Icon and image helper utilities
 * Centralized icon/image handling to eliminate duplication
 */

/**
 * Get the public URL path for an image
 * @param {string} imagePath - The image path relative to public folder (e.g., '/images/logo.png')
 * @returns {string} - Full path with PUBLIC_URL prefix
 */
const getPublicImagePath = (imagePath) => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${publicUrl}${cleanPath}`;
};

// Source icon mapping for Source/ReportedFrom fields
const sourceIconMap = {
  'Facebook': getPublicImagePath('/images/facebook.png'),
  'Instagram': getPublicImagePath('/images/instagram.png'),
  'Reddit': getPublicImagePath('/images/reddit.png'),
  'TikTok': getPublicImagePath('/images/tiktok.png'),
  'Twitter': getPublicImagePath('/images/twitter.png'),
  'YouTube': getPublicImagePath('/images/youtube.png'),
  'CyberWell': getPublicImagePath('/images/cyberwell-logo.png'),
};

/**
 * Get social platform icon by source name (case-insensitive)
 * @param {string} source - The source/platform name
 * @returns {string|null} - Icon path or null if not found
 */
export const getSocialPlatformIcon = (source) => {
  if (!source) return null;
  
  const sourceLower = source.toLowerCase();
  
  // Check for common platform names in the source string
  if (sourceLower.includes('facebook')) return getPublicImagePath('/images/facebook.png');
  if (sourceLower.includes('instagram')) return getPublicImagePath('/images/instagram.png');
  if (sourceLower.includes('tiktok')) return getPublicImagePath('/images/tiktok.png');
  if (sourceLower.includes('twitter') || sourceLower.includes('x.com')) return getPublicImagePath('/images/twitter.png');
  if (sourceLower.includes('youtube')) return getPublicImagePath('/images/youtube.png');
  if (sourceLower.includes('reddit')) return getPublicImagePath('/images/reddit.png');
  if (sourceLower.includes('chrome')) return getPublicImagePath('/images/chrome_extension.png');
  if (sourceLower.includes('leviathan')) return getPublicImagePath('/images/Leviathan.png');
  if (sourceLower.includes('cyberwell')) return getPublicImagePath('/images/cyberwell-logo.png');
  
  return null;
};

/**
 * Get source icon using centralized sourceIconMap
 * @param {string} sourceName - The source name
 * @returns {string|null} - Icon path or null if not found
 */
export const getSourceIconByName = (sourceName) => {
  if (!sourceName) return null;
  
  // Direct match first
  let iconSrc = sourceIconMap[sourceName] || null;
  
  // Fallback: case-insensitive match
  if (!iconSrc && typeof sourceName === 'string') {
    const lower = sourceName.toLowerCase();
    const matchedKey = Object.keys(sourceIconMap).find(key => 
      key.toLowerCase() === lower
    );
    if (matchedKey) {
      iconSrc = sourceIconMap[matchedKey];
    }
  }
  
  // If still not found, try pattern matching
  if (!iconSrc) {
    iconSrc = getSocialPlatformIcon(sourceName);
  }
  
  return iconSrc;
};
